import React from "react";
import {
  Card,
  Divider,
  Grid,
  Icons,
  Loader,
  Modal,
  Text,
} from "@pushpress/shared-components";
import {
  useAppointmentTypeQuery,
  useAppointmentTypeStaffQuery,
} from "../../../apis/appointmentApi.hooks";
import InfoModalCard from "../../../components/modals/InfoModalCard";
import InfoModalHeader from "../../../components/modals/InfoModalHeader";
import ColorSquare from "../../../components/common/ColorSquare/ColorSquare";
import {
  convertSecondsToLargestWholeUnit,
  getShortDayofTheWeek,
  joinStrings,
} from "../../../utils/misc";
import styles from "./AppointmentTypeInfoModal.module.scss";

interface AppointmentTypeInfoModalProps {
  appointmentTypeUuid?: string;
  visible: boolean;
  onClose: VoidFunction;
}

const AppointmentTypeInfoModal = (props: AppointmentTypeInfoModalProps) => {
  const { visible, onClose } = props;

  return (
    <Modal visible={visible} closeModal={onClose}>
      <AppointmentTypeInfoModalContent {...props} />
    </Modal>
  );
};

const AppointmentTypeInfoModalContent = (
  props: AppointmentTypeInfoModalProps,
) => {
  const { appointmentTypeUuid, onClose } = props;

  const appointmentTypeQuery = useAppointmentTypeQuery(appointmentTypeUuid!, {
    enabled: !!appointmentTypeUuid,
  });

  const staff = useAppointmentTypeStaffQuery(appointmentTypeUuid!, {
    enabled: !!appointmentTypeUuid,
  });

  const appointmentType = appointmentTypeQuery.data?.data;

  if (!appointmentType || !staff?.data) {
    return (
      <InfoModalCard>
        <Loader className={styles.loader} />
      </InfoModalCard>
    );
  }

  // convertions
  const duration = convertSecondsToLargestWholeUnit(appointmentType.duration);
  const minimumSchedulingNotice = convertSecondsToLargestWholeUnit(
    appointmentType.minimumSchedulingNotice,
  );
  const lateCancel = convertSecondsToLargestWholeUnit(
    appointmentType.lateCancel,
  );
  const textReminderTiming = convertSecondsToLargestWholeUnit(
    appointmentType.textReminderTiming,
  );
  const emailReminderTiming = convertSecondsToLargestWholeUnit(
    appointmentType.emailReminderTiming,
  );
  const staffList = appointmentType.staff.map(
    ({ uuid }) => staff.data?.staff.find((item) => item.userUuid === uuid),
  );
  const staffNames = staffList
    .filter((item) => !!item)
    .map((item) => joinStrings(item?.firstName, item?.lastName))
    .join(", ");

  return (
    <InfoModalCard>
      <Card.Content>
        <InfoModalHeader onClose={onClose} />
        <Grid container direction="column" columnSpacing={2}>
          <Grid item>
            <Grid container alignItems="center">
              <Grid item xs={2} container>
                <ColorSquare color={appointmentType.color} />
              </Grid>
              <Grid item xs={10}>
                <Text variant="heading-3">{appointmentType.name}</Text>
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <Grid container alignItems="center">
              <Grid item xs={2}>
                <Icons.Clock />
              </Grid>
              <Grid item xs={10}>
                <Text variant="body-lg">{`${duration.value} ${duration.unit}`}</Text>
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <Grid container>
              <Grid item xs={2}>
                <Icons.Users />
              </Grid>
              <Grid item xs={10} container alignItems="center">
                <Text variant="body-lg">{staffNames}</Text>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Card.Content>
      <Divider />
      <Card.Content>
        <Grid container direction="column" columnSpacing={3} paddingY={1}>
          {appointmentType.availability && (
            <Grid item container justifyContent="space-between" spacing={0}>
              <Grid item>
                <Text variant="body-lg">Availability</Text>
              </Grid>
              <Grid item>
                <Grid container direction="column" columnSpacing={2}>
                  {appointmentType.availability.map((timeSlot, index) => (
                    <Grid item key={index}>
                      <Text
                        variant="body-lg"
                        align="right"
                      >{`${getShortDayofTheWeek(
                        timeSlot.dayOfTheWeek,
                      )}, ${timeSlot.startTime
                        .toLowerCase()
                        .replace(" ", "")} - ${timeSlot.endTime
                        .toLowerCase()
                        .replace(" ", "")}`}</Text>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          )}
          {appointmentType.minimumSchedulingNotice && (
            <>
              <Grid item>
                <Divider />
              </Grid>
              <Grid item container justifyContent="space-between" spacing={0}>
                <Grid item>
                  <Text variant="body-lg">Minimum scheduling notice</Text>
                </Grid>
                <Grid item>
                  <Text variant="body-lg">{`${minimumSchedulingNotice.value} ${minimumSchedulingNotice.unit} before`}</Text>
                </Grid>
              </Grid>
            </>
          )}
          {appointmentType.lateCancel && (
            <>
              <Grid item>
                <Divider />
              </Grid>
              <Grid item container justifyContent="space-between" spacing={0}>
                <Grid item>
                  <Text variant="body-lg">Late cancel</Text>
                </Grid>
                <Grid item>
                  <Text variant="body-lg">{`${lateCancel.value} ${lateCancel.unit} before`}</Text>
                </Grid>
              </Grid>
            </>
          )}
          <Grid item>
            <Divider />
          </Grid>
          <Grid item container justifyContent="space-between" spacing={0}>
            <Grid item>
              <Text variant="body-lg">Email confirmation</Text>
            </Grid>
            <Grid item>
              <Text variant="body-lg">
                {appointmentType.sendEmailNotification ? "Yes" : "No"}
              </Text>
            </Grid>
          </Grid>
          {appointmentType.sendTextReminder &&
            appointmentType.textReminderTiming && (
              <>
                <Grid item>
                  <Divider />
                </Grid>
                <Grid item container justifyContent="space-between" spacing={0}>
                  <Grid item>
                    <Text variant="body-lg">Text Reminder</Text>
                  </Grid>
                  <Grid item>
                    <Text variant="body-lg">{`${textReminderTiming.value} ${textReminderTiming.unit} before`}</Text>
                  </Grid>
                </Grid>
              </>
            )}
          {appointmentType.sendEmailReminder &&
            appointmentType.emailReminderTiming && (
              <>
                <Grid item>
                  <Divider />
                </Grid>
                <Grid item container justifyContent="space-between" spacing={0}>
                  <Grid item>
                    <Text variant="body-lg">Email reminder</Text>
                  </Grid>
                  <Grid item>
                    <Text variant="body-lg">{`${emailReminderTiming.value} ${emailReminderTiming.unit} before`}</Text>
                  </Grid>
                </Grid>
              </>
            )}
        </Grid>
      </Card.Content>
    </InfoModalCard>
  );
};

export default AppointmentTypeInfoModal;
